<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import SharedFiles from './SharedFiles.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import InvoicesList from 'dashboard/components/widgets/conversation/invoices/InvoicesList.vue';
import TicketDetailSection from 'dashboard/routes/dashboard/tickets/components/TicketDetailSection.vue';
import TrackerLinkSection from 'dashboard/routes/dashboard/tickets/components/TrackerLinkSection.vue';
import ConversationCasesSection from 'dashboard/components-next/SupportCases/ConversationCasesSection.vue';
import {
  conversationCaseTitle,
  conversationRequester,
} from 'dashboard/composables/useCaseCreateDialog';
import TripsList from 'dashboard/components-next/Conversation/TripsList.vue';
import ActionApprovalList from 'dashboard/components-next/Conversation/ActionApprovalList.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const {
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);
const conversationSidebarItems = ref([]);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled, accountId } = useAccount();

const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// The ticket engine (SupportCase domain) is its own feature behind its own
// flag: its endpoints 404 while the flag is off, so the section is not rendered
// at all rather than left to fail quietly.
const isSupportCasesEnabled = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.SUPPORT_CASES)
);

// The legacy conversation-lens ticket sections. The engine's section is the
// panel's "Tickets" wherever the engine is on, so the lens hides rather than
// stack a second, differently-shaped ticket block under the same noun.
const isLegacyTicketsEnabled = computed(
  () =>
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.TICKETS) &&
    !isSupportCasesEnabled.value
);

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const khaltiIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'khalti'
);

const isPaymentsEnabled = computed(
  () =>
    isCloudFeatureEnabled(FEATURE_FLAGS.PAYMENTS) &&
    !!khaltiIntegration.value?.enabled
);

// The flag alone, not an enabled GDS connection: a trip is captured by an agent and lives
// in our own tables, so the panel is useful before anyone has connected a GDS - which is
// exactly the state an agency is in while being onboarded.
const isTravelEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.TRAVEL)
);

// Raised by TripsList so the header can carry a dot while the panel is collapsed. An
// expiring hold that only shows once you expand the accordion is the deadline being missed
// in a different way.
const tripAttention = ref(null);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
// The ticket stamp (mirrors Conversation#ticket?): a ticket gets the ticket
// head section, a plain chat gets the tracker-ticket links instead.
const isTicket = computed(
  () => !!currentChat.value?.additional_attributes?.ticket
);
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

// AI actions the governed gate held back for a human decision. The panel owns the fetch and the
// filter so the accordion can be left out entirely when nothing is pending - a section header
// that opens onto "nothing to approve" is a decision surface that cries wolf.
//
// Its accordion is keyed on `is_ai_action_approvals_closed` rather than the usual `_open`, so it
// starts expanded: the section only exists while something is waiting on a decision, and the
// default-collapsed state every other section has would hide the two buttons that resolve it.
const isTolaAiEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.TOLA_AI)
);

const actionApprovals = useMapGetter('captainActionApprovals/getRecords');

const pendingActionApprovals = computed(() =>
  actionApprovals.value.filter(
    approval =>
      approval.status === 'pending' &&
      approval.conversation_id === Number(conversationId.value)
  )
);

const fetchActionApprovals = () => {
  if (!isTolaAiEnabled.value) return;
  // Silent on failure: an agent opened this conversation to answer a customer, and the two
  // decision buttons surface their own errors when they are actually pressed.
  store
    .dispatch('captainActionApprovals/get', { status: 'pending' })
    .catch(() => {});
};

watch(conversationId, fetchActionApprovals, { immediate: true });

const channelType = computed(() => currentChat.value.meta?.channel);

const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() => inboxGetter.value(props.inboxId));

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
    store.dispatch('contactConversations/get', contactId.value);
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
});
</script>

<template>
  <div class="w-full">
    <!-- Closing lives with the Details / Tola AI switcher above (SidepanelTabs). -->
    <SidebarActionsHeader
      :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
      :closable="false"
    />
    <TicketDetailSection
      v-if="isLegacyTicketsEnabled && isTicket && currentChat.id"
      :conversation="currentChat"
    />
    <ContactInfo
      :contact="contact"
      :inbox="inbox"
      :channel-type="channelType"
    />
    <TrackerLinkSection
      v-if="isLegacyTicketsEnabled && !isTicket && currentChat.id"
      :conversation-id="currentChat.id"
    />
    <ConversationCasesSection
      v-if="isSupportCasesEnabled && currentChat.id"
      :conversation-id="currentChat.id"
      :initial-title="conversationCaseTitle(currentChat)"
      :requester-contact="conversationRequester(currentChat)"
    />
    <div class="pb-8 list-group">
      <Draggable
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div
            v-if="
              element.name === 'ai_action_approvals' &&
              isTolaAiEnabled &&
              pendingActionApprovals.length
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.AI_ACTION_APPROVALS')"
              :is-open="
                !isContactSidebarItemOpen('is_ai_action_approvals_closed')
              "
              compact
              @toggle="
                () => toggleSidebarUIState('is_ai_action_approvals_closed')
              "
            >
              <ActionApprovalList
                :approvals="pendingActionApprovals"
                @decided="fetchActionApprovals"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @toggle="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <ConversationParticipant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'conversation_info'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <CustomAttributes
                attribute-type="contact_attribute"
                attribute-from="conversation_contact_panel"
                :contact-id="contact.id"
                :empty-state-message="
                  $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                "
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <AccordionItem
              v-if="contact.id"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <ContactHistory
                :contact-id="contact.id"
                :exclude-conversation-id="conversationId"
              />
            </AccordionItem>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>
          <div
            v-else-if="
              element.name === 'linear_issues' &&
              isLinearFeatureEnabled &&
              isLinearClientIdConfigured
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_linear_issues_open', value)
              "
            >
              <LinearSetupCTA v-if="!isLinearConnected" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shopify_orders_open', value)
              "
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'travel_trips' && isTravelEnabled">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.TRAVEL_TRIPS')"
              :is-open="isContactSidebarItemOpen('is_travel_trips_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_travel_trips_open', value)
              "
            >
              <template #button>
                <span
                  v-if="tripAttention"
                  class="rounded-full size-2"
                  :class="
                    tripAttention === 'alert' ? 'bg-n-ruby-9' : 'bg-n-amber-9'
                  "
                />
              </template>
              <TripsList
                :contact-id="contactId"
                :conversation-id="currentChat?.id"
                @attention="value => (tripAttention = value)"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'invoices' && isPaymentsEnabled">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.INVOICES')"
              :is-open="isContactSidebarItemOpen('is_invoices_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_invoices_open', value)"
            >
              <InvoicesList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_notes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
              :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_contact_notes_open', value)
              "
            >
              <ContactNotes :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'shared_files'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHARED_FILES')"
              :is-open="isContactSidebarItemOpen('is_shared_files_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shared_files_open', value)
              "
            >
              <SharedFiles />
            </AccordionItem>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.contact--profile) {
  @apply pb-3 border-b border-solid border-n-weak;
}
</style>
